// Dr.Rx™ National FDA/CDC/DEA Regulatory Alerts Studio
import React, { useState, useEffect } from 'react';
import { getRawNationalAlerts } from '../../services/alertService';
import {
    triggerNationalAlertsUpdate,
    saveNationalAlert,
    deleteNationalAlert,
    getAlertsConfig,
    setAlertsConfig,
    testAlertSources
} from '../../services/adminService';
import type { Alert } from '../../types';
import { useToast } from '../../contexts/ToastContext';

const RefreshIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h5M20 20v-5h-5M4 4l1.5 1.5A9 9 0 0120.5 15M20 20l-1.5-1.5A9 9 0 003.5 9" />
    </svg>
);
const PlusIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
);
const BugIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
);

const AlertsStudioWidget: React.FC = () => {
    const { showToast, confirmAction } = useToast();
    const [alerts, setAlerts] = useState<Alert[]>([]);
    const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isScraping, setIsScraping] = useState(false);
    const [isTesting, setIsTesting] = useState(false);
    const [fallbackActive, setFallbackActive] = useState(false);
    const [allowPublicRead, setAllowPublicRead] = useState(true);
    const [diagnosticReport, setDiagnosticReport] = useState<any | null>(null);

    // Editor Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingAlert, setEditingAlert] = useState<Partial<Alert>>({
        source: 'FDA',
        severity: 'High',
        date: new Date().toISOString().split('T')[0]
    });

    const loadData = async () => {
        setIsLoading(true);
        try {
            const { alerts: fetchedAlerts, lastUpdated: updated } = await getRawNationalAlerts();
            setAlerts(fetchedAlerts);
            setLastUpdated(updated);

            const config = await getAlertsConfig();
            setFallbackActive(config.isFrontendFallbackActive);
            setAllowPublicRead(config.allowPublicRead !== false);
        } catch (e: any) {
            console.error('Failed to load alerts studio data:', e);
            showToast(`Failed to load alerts: ${e.message}`, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const handleScrapeNational = async () => {
        setIsScraping(true);
        try {
            const res: any = await triggerNationalAlertsUpdate();
            showToast(res?.message || 'National FDA/CDC/DEA alerts ingested.', 'success');
            await loadData();
        } catch (e: any) {
            showToast(`Ingestion failed: ${e.message}`, 'error');
        } finally {
            setIsScraping(false);
        }
    };

    const handleRunDiagnostics = async () => {
        setIsTesting(true);
        setDiagnosticReport(null);
        try {
            const report = await testAlertSources();
            setDiagnosticReport(report);
            showToast('Upstream feed diagnostics completed.', 'info');
        } catch (e: any) {
            showToast(`Diagnostic failed: ${e.message}`, 'error');
        } finally {
            setIsTesting(false);
        }
    };

    const handleToggleFallback = async () => {
        const newState = !fallbackActive;
        await setAlertsConfig({ isFrontendFallbackActive: newState });
        setFallbackActive(newState);
        showToast(
            `Emergency fallback mode ${newState ? 'enabled' : 'disabled'}.`,
            newState ? 'warning' : 'success'
        );
    };

    const handleTogglePublicRead = async () => {
        const newState = !allowPublicRead;
        await setAlertsConfig({ allowPublicRead: newState });
        setAllowPublicRead(newState);
        showToast(`Guest alert visibility ${newState ? 'enabled' : 'restricted'}.`, 'info');
    };

    const handleDelete = async (id: string) => {
        const confirmed = await confirmAction({
            title: 'Delete Regulatory Alert',
            message: 'Are you sure you want to permanently remove this alert from the live national feed?',
            confirmLabel: 'Delete Alert',
            variant: 'danger'
        });
        if (!confirmed) return;

        try {
            await deleteNationalAlert(id);
            setAlerts(prev => prev.filter(a => a.id !== id));
            showToast('Alert removed from national feed.', 'success');
        } catch (e: any) {
            showToast(`Failed to delete alert: ${e.message}`, 'error');
        }
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingAlert.title || !editingAlert.summary) return;

        const newAlert: Alert = {
            id: editingAlert.id || `manual-${Date.now()}`,
            source: (editingAlert.source as any) || 'FDA',
            severity: (editingAlert.severity as any) || 'High',
            title: editingAlert.title,
            summary: editingAlert.summary,
            details: editingAlert.details || editingAlert.summary,
            date: editingAlert.date || new Date().toISOString(),
            link: editingAlert.link || '#'
        };

        try {
            await saveNationalAlert(newAlert);
            setIsModalOpen(false);
            showToast('National alert saved and published.', 'success');
            await loadData();
        } catch (err: any) {
            showToast(`Failed to save alert: ${err.message}`, 'error');
        }
    };

    return (
        <div className="space-y-6">
            {/* Control Bar */}
            <div className="bg-[#15243E] p-5 rounded-2xl border border-blue-900/50 flex flex-wrap gap-4 justify-between items-center">
                <div>
                    <h3 className="text-lg font-bold text-white">National Regulatory Alerts Ingestion (FDA / CDC / DEA)</h3>
                    <p className="text-xs text-gray-400 mt-0.5">
                        Manage live regulatory feeds, upstream RSS/API diagnostics, and manual clinical advisories.
                    </p>
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex items-center gap-2 bg-black/20 px-3 py-1.5 rounded-xl border border-blue-900/30">
                        <span className="text-xs text-gray-400 font-bold">Public Visibility:</span>
                        <button
                            onClick={handleTogglePublicRead}
                            className={`text-xs font-bold px-2 py-0.5 rounded transition-colors ${
                                allowPublicRead ? 'bg-blue-500 text-white' : 'bg-gray-700 text-gray-400'
                            }`}
                        >
                            {allowPublicRead ? 'PUBLIC' : 'AUTH ONLY'}
                        </button>
                    </div>

                    <div className="flex items-center gap-2 bg-black/20 px-3 py-1.5 rounded-xl border border-blue-900/30">
                        <span className="text-xs text-gray-400 font-bold">Fallback Mode:</span>
                        <button
                            onClick={handleToggleFallback}
                            className={`text-xs font-bold px-2 py-0.5 rounded transition-colors ${
                                fallbackActive ? 'bg-red-500 text-white' : 'bg-emerald-500/20 text-emerald-400'
                            }`}
                        >
                            {fallbackActive ? 'ACTIVE (MOCK)' : 'OFF (LIVE DB)'}
                        </button>
                    </div>

                    <button
                        onClick={handleRunDiagnostics}
                        disabled={isTesting}
                        className="flex items-center gap-2 bg-purple-600/80 hover:bg-purple-500 text-white text-xs font-bold px-3.5 py-2 rounded-xl transition-all shadow-lg disabled:opacity-50"
                    >
                        <BugIcon /> {isTesting ? 'Testing Feeds...' : 'Test Upstream Feeds'}
                    </button>

                    <button
                        onClick={handleScrapeNational}
                        disabled={isScraping}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold px-4 py-2 rounded-xl transition-all shadow-lg disabled:opacity-50"
                    >
                        <RefreshIcon /> {isScraping ? 'Ingesting...' : 'Force Scrape Now'}
                    </button>

                    <button
                        onClick={() => {
                            setEditingAlert({
                                source: 'FDA',
                                severity: 'High',
                                date: new Date().toISOString().split('T')[0]
                            });
                            setIsModalOpen(true);
                        }}
                        className="flex items-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-gray-950 text-xs font-bold px-4 py-2 rounded-xl transition-all shadow-lg"
                    >
                        <PlusIcon /> Post Alert
                    </button>
                </div>
            </div>

            {/* Diagnostic Report Panel */}
            {diagnosticReport && (
                <div className="bg-black/40 border border-purple-500/50 rounded-2xl p-6 animate-fade-in">
                    <div className="flex justify-between items-start mb-4">
                        <h4 className="text-purple-300 font-bold text-sm uppercase tracking-wider">
                            Upstream Source Diagnostic Report
                        </h4>
                        <button onClick={() => setDiagnosticReport(null)} className="text-gray-500 hover:text-white text-xs">
                            Close
                        </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs font-mono">
                        {Object.entries(diagnosticReport).map(([source, data]: [string, any]) => (
                            <div
                                key={source}
                                className={`p-4 rounded-xl border ${
                                    data.status === 'OK' || data.status === 'Fallback'
                                        ? 'bg-emerald-900/10 border-emerald-500/30'
                                        : 'bg-red-900/10 border-red-500/30'
                                }`}
                            >
                                <div className="flex justify-between font-bold mb-2">
                                    <span className="uppercase text-white text-sm">{source}</span>
                                    <span className={data.status === 'OK' ? 'text-emerald-400' : 'text-yellow-400'}>
                                        {data.status}
                                    </span>
                                </div>
                                <div className="space-y-1 text-gray-300">
                                    <p>Method: {data.method}</p>
                                    <p className="tabular-nums">Items Found: {data.count}</p>
                                    {data.sample && <p className="truncate text-gray-400" title={data.sample}>Sample: “{data.sample}”</p>}
                                    {data.error && <p className="text-red-300 bg-red-900/20 p-1 rounded mt-2">{data.error}</p>}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* National Alerts Table */}
            <div className="bg-[#1E2E4A] rounded-2xl border border-blue-900/50 overflow-hidden">
                <div className="p-4 bg-[#15243E] border-b border-blue-900/50 flex justify-between items-center text-xs text-gray-400">
                    <span className="font-semibold text-gray-300 tabular-nums">
                        Showing {alerts.length} active alerts in database
                    </span>
                    <span className="tabular-nums">
                        Last Ingested: {lastUpdated ? lastUpdated.toLocaleString() : 'Never'}
                    </span>
                </div>

                {isLoading ? (
                    <div className="p-12 text-center text-gray-400">Loading national regulatory alerts...</div>
                ) : alerts.length === 0 ? (
                    <div className="p-12 text-center text-gray-400">
                        <p className="mb-2 font-semibold text-white">Database is currently empty (0 live alerts).</p>
                        <p className="text-xs text-gray-400">
                            Click “Force Scrape Now” to ingest FDA/CDC/DEA feeds or “Post Alert” to publish a manual advisory.
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-black/20 text-gray-400 uppercase text-xs">
                                <tr>
                                    <th className="p-4">Source</th>
                                    <th className="p-4">Date</th>
                                    <th className="p-4">Title & Summary</th>
                                    <th className="p-4">Severity</th>
                                    <th className="p-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-blue-900/30">
                                {alerts.map(alert => (
                                    <tr key={alert.id} className="hover:bg-white/5 transition-colors">
                                        <td className="p-4 font-bold text-white">
                                            <span
                                                className={`px-2 py-1 rounded text-xs ${
                                                    alert.source === 'FDA'
                                                        ? 'bg-blue-500/20 text-blue-300'
                                                        : alert.source === 'CDC'
                                                        ? 'bg-teal-500/20 text-teal-300'
                                                        : 'bg-amber-500/20 text-amber-300'
                                                }`}
                                            >
                                                {alert.source}
                                            </span>
                                        </td>
                                        <td className="p-4 text-gray-400 font-mono text-xs whitespace-nowrap tabular-nums">
                                            {alert.date.split('T')[0]}
                                        </td>
                                        <td className="p-4 max-w-xl">
                                            <div className="font-bold text-white line-clamp-1">{alert.title}</div>
                                            <div className="text-gray-400 text-xs line-clamp-1 mt-0.5">{alert.summary}</div>
                                        </td>
                                        <td className="p-4">
                                            <span
                                                className={`text-xs font-bold ${
                                                    alert.severity === 'High' ? 'text-red-400' : 'text-yellow-400'
                                                }`}
                                            >
                                                {alert.severity}
                                            </span>
                                        </td>
                                        <td className="p-4 text-right whitespace-nowrap">
                                            <button
                                                onClick={() => {
                                                    setEditingAlert({
                                                        ...alert,
                                                        date: alert.date.split('T')[0]
                                                    });
                                                    setIsModalOpen(true);
                                                }}
                                                className="text-blue-400 hover:text-blue-300 font-semibold text-xs mr-4"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleDelete(alert.id)}
                                                className="text-red-400 hover:text-red-300 font-semibold text-xs"
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Create / Edit Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-[#15243E] border border-blue-900 rounded-2xl w-full max-w-lg p-6 shadow-2xl">
                        <h3 className="text-xl font-bold text-white mb-4">
                            {editingAlert.id ? 'Edit Regulatory Alert' : 'Post Manual Regulatory Alert'}
                        </h3>
                        <form onSubmit={handleSave} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs text-gray-400 mb-1">Agency Source</label>
                                    <select
                                        value={editingAlert.source}
                                        onChange={e => setEditingAlert({ ...editingAlert, source: e.target.value as any })}
                                        className="w-full bg-[#101D35] border border-blue-900 rounded-lg p-2.5 text-white text-sm"
                                    >
                                        <option value="FDA">FDA</option>
                                        <option value="CDC">CDC</option>
                                        <option value="DEA">DEA</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 mb-1">Clinical Severity</label>
                                    <select
                                        value={editingAlert.severity}
                                        onChange={e => setEditingAlert({ ...editingAlert, severity: e.target.value as any })}
                                        className="w-full bg-[#101D35] border border-blue-900 rounded-lg p-2.5 text-white text-sm"
                                    >
                                        <option value="High">High</option>
                                        <option value="Medium">Medium</option>
                                        <option value="Low">Low</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs text-gray-400 mb-1">Headline</label>
                                <input
                                    type="text"
                                    required
                                    value={editingAlert.title || ''}
                                    onChange={e => setEditingAlert({ ...editingAlert, title: e.target.value })}
                                    className="w-full bg-[#101D35] border border-blue-900 rounded-lg p-2.5 text-white text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-400 mb-1">Clinical Summary</label>
                                <textarea
                                    required
                                    value={editingAlert.summary || ''}
                                    onChange={e => setEditingAlert({ ...editingAlert, summary: e.target.value })}
                                    className="w-full bg-[#101D35] border border-blue-900 rounded-lg p-2.5 text-white text-sm h-24"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs text-gray-400 mb-1">Date</label>
                                    <input
                                        type="date"
                                        value={editingAlert.date}
                                        onChange={e => setEditingAlert({ ...editingAlert, date: e.target.value })}
                                        className="w-full bg-[#101D35] border border-blue-900 rounded-lg p-2.5 text-white text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-400 mb-1">Official Source URL</label>
                                    <input
                                        type="text"
                                        value={editingAlert.link || ''}
                                        onChange={e => setEditingAlert({ ...editingAlert, link: e.target.value })}
                                        placeholder="https://www.fda.gov/..."
                                        className="w-full bg-[#101D35] border border-blue-900 rounded-lg p-2.5 text-white text-sm"
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="px-4 py-2 text-gray-400 hover:text-white text-sm font-semibold"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="bg-yellow-400 hover:bg-yellow-300 text-gray-950 font-bold px-6 py-2 rounded-xl text-sm shadow-md"
                                >
                                    Save & Publish
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AlertsStudioWidget;
